import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardCheck, Check, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Clearance() {
  const [records, setRecords] = useState([]);
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isManagement = ['admin', 'team_leader'].includes(user.role);

  const fetch = useCallback(async () => {
    try { const res = await axios.get(`${API}/exit/clearance`, { headers: getHeaders() }); setRecords(res.data); } catch (e) { toast.error('Failed'); }
  }, []);
  useEffect(() => { fetch(); }, []);

  const handleClear = async (recordId, dept, action) => {
    try { await axios.put(`${API}/exit/clearance/${recordId}/department`, { department: dept, action }, { headers: getHeaders() }); toast.success(`${dept} ${action}`); fetch(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div data-testid="clearance-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}><ClipboardCheck className="inline-block mr-2 mb-1" size={32} />Clearance Management</h1>
        <p className="text-slate-500 text-sm">Department-wise clearance tracking for exiting employees</p>
      </div>
      {records.length === 0 ? <p className="text-center text-slate-400 py-12">No clearance records. Clearance is auto-created when a resignation is approved.</p> : (
        <div className="space-y-4">
          {records.map((r, i) => (
            <div key={r.id || i} data-testid={`clearance-card-${i}`} className="bg-white border border-blue-100 rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-bold text-blue-900 text-lg">{r.employee_name}</p>
                  <p className="text-xs text-slate-500">Clearance ID: {r.id?.slice(0, 8)}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${r.overall_status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  {r.overall_status === 'completed' ? 'All Clear' : 'Pending'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {(r.departments || []).map((d, di) => (
                  <div key={d.name || di} data-testid={`dept-${r.id}-${d.name}`} className={`border rounded-lg p-3 text-center ${d.status === 'cleared' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                    <p className="text-xs font-bold text-blue-900 mb-1">{d.name}</p>
                    {d.status === 'cleared' ? (
                      <div><Check size={16} className="mx-auto text-emerald-600 mb-0.5" /><p className="text-[10px] text-emerald-600">{d.cleared_by}</p></div>
                    ) : (
                      <div>
                        <AlertTriangle size={16} className="mx-auto text-amber-500 mb-0.5" />
                        {isManagement && <Button size="sm" onClick={() => handleClear(r.id, d.name, 'cleared')} className="bg-emerald-600 text-white text-[10px] h-6 mt-1 px-2"><Check size={10} className="mr-0.5" />Clear</Button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
